export interface LayerViewState {
  visible: boolean;
  opacity: number;
  labels: boolean;
}

export function layerPreferenceKey(accountId: string, projectId: string): string | null {
  return accountId && projectId ? `mapLayers:${accountId}:${projectId}` : null;
}

export function resolveLayerState(
  defaults: LayerViewState,
  overrides: Partial<LayerViewState> = {},
  groupVisible = true,
): LayerViewState {
  const visible = typeof overrides?.visible === 'boolean' ? overrides.visible : defaults.visible;
  const opacity = typeof overrides?.opacity === 'number' && Number.isFinite(overrides.opacity)
    && overrides.opacity >= 0 && overrides.opacity <= 1 ? overrides.opacity : defaults.opacity;
  return {
    visible: groupVisible && visible,
    opacity,
    labels: typeof overrides?.labels === 'boolean' ? overrides.labels : defaults.labels,
  };
}

/**
 * The basemap each user last chose on a project, shared by the project map and the design-context Location picker
 * (mobile keeps the same choice in projectMapViewCache). Browser storage, like the legacy layer overrides above:
 * published layer visibility lives server-side (MapReferenceLayerService preferences), the basemap has no api slot.
 */
export function basemapPreferenceKey(accountId: string, projectId: string): string | null {
  return accountId && projectId ? `mapBasemap:${accountId}:${projectId}` : null;
}

type BasemapStorage = Pick<Storage, 'getItem' | 'setItem'>;
const browserStorage = (): (BasemapStorage & Pick<Storage, 'removeItem'>) | null => (typeof localStorage === 'undefined' ? null : localStorage);

export function loadBasemapPreference(accountId: string, projectId: string, storage: BasemapStorage | null = browserStorage()): string | null {
  const key = basemapPreferenceKey(accountId, projectId);
  if (!key || !storage) return null;
  try { return storage.getItem(key) || null; } catch { return null; }
}

export function saveBasemapPreference(accountId: string, projectId: string, basemapId: string,
  storage: BasemapStorage | null = browserStorage()): void {
  const key = basemapPreferenceKey(accountId, projectId);
  if (!key || !storage || !basemapId) return;
  try { storage.setItem(key, basemapId); } catch { /* The maps still switch basemaps without storage. */ }
}

/** 7.2: the project map's Stationing layer, on unless the user turned it off on that project (browser storage, like the basemap) */
export function stationingPreferenceKey(accountId: string, projectId: string): string | null {
  return accountId && projectId ? `mapStationing:${accountId}:${projectId}` : null;
}

export function loadStationingPreference(accountId: string, projectId: string, storage: BasemapStorage | null = browserStorage()): boolean {
  const key = stationingPreferenceKey(accountId, projectId);
  if (!key || !storage) return true;
  try { return storage.getItem(key) !== 'off'; } catch { return true; }
}

export function saveStationingPreference(accountId: string, projectId: string, visible: boolean,
  storage: BasemapStorage | null = browserStorage()): void {
  const key = stationingPreferenceKey(accountId, projectId);
  if (!key || !storage) return;
  try { storage.setItem(key, visible ? 'on' : 'off'); } catch { /* The layer still toggles without storage. */ }
}

/** 7.2 §3c: the alignment the user locked Stationing to on a project (its name), or null for Automatic (the nearest) */
export function stationingAlignmentKey(accountId: string, projectId: string): string | null {
  return accountId && projectId ? `mapStationingAlignment:${accountId}:${projectId}` : null;
}

export function loadStationingAlignment(accountId: string, projectId: string, storage: BasemapStorage | null = browserStorage()): string | null {
  const key = stationingAlignmentKey(accountId, projectId);
  if (!key || !storage) return null;
  try { return storage.getItem(key) || null; } catch { return null; }
}

export function saveStationingAlignment(accountId: string, projectId: string, name: string | null,
  storage: (BasemapStorage & Partial<Pick<Storage, 'removeItem'>>) | null = browserStorage()): void {
  const key = stationingAlignmentKey(accountId, projectId);
  if (!key || !storage) return;
  try {
    if (name) storage.setItem(key, name);
    else if (storage.removeItem) storage.removeItem(key);
    else storage.setItem(key, '');
  } catch { /* the choice still applies for this visit */ }
}

/**
 * The object `EsriMapComponent.selectBasemap` takes for a saved id: the built-in Default and Satellite (as
 * MapControlsComponent lists them), else the project's organization basemap with that id; null if it is gone.
 */
export function resolveBasemap<T extends { id?: string }>(basemapId: string | null, organizationBasemaps: T[] = []): T | { id: string } | null {
  if (!basemapId) return null;
  if (basemapId === 'default' || basemapId === 'satellite') return { id: basemapId };
  return organizationBasemaps.find(basemap => basemap?.id === basemapId) || null;
}
